/**
 * AudioTrack Component
 *
 * Displays audio tracks on the timeline with:
 * - Waveform visualization (fixed, reveals when trimming)
 * - Draggable position
 * - Trim handles for adjusting start/end
 * - Volume/mute controls
 * - Selection handling
 */

import { useCallback, useRef, useState, useEffect, memo } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { usePlaybackStore } from '@/stores/playbackStore';
import { base64ToUint8Array } from '@/utils/audioWaveform';
import type { AudioTrack } from '@/types';

interface AudioTrackProps {
  track: AudioTrack;
  pixelsPerSecond: number;
  isSelected: boolean;
  onSelect: () => void;
}

/**
 * Draw waveform on canvas
 * The waveform represents the FULL audio file and stays anchored
 */
function drawWaveformOnCanvas(
  canvas: HTMLCanvasElement,
  track: AudioTrack,
  isMuted: boolean,
  displayWidth: number,
  pixelsPerSecond: number
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx || !track.waveform) return;
  
  // Decode base64 samples
  let samples: Uint8Array;
  try {
    samples = base64ToUint8Array(track.waveform.samples);
  } catch (err) {
    console.error('[AudioTrack] Failed to decode waveform:', err);
    return;
  }
  
  const height = canvas.height;
  
  // Calculate the full width of the waveform (entire audio duration)
  const fullDuration = track.duration;
  const fullWidth = fullDuration * pixelsPerSecond;
  
  // Calculate trim positions in pixels (offset from the left edge of the clip)
  const trimStartPx = track.trimStart * pixelsPerSecond;
  
  ctx.clearRect(0, 0, canvas.width, height);
  
  // We need to draw the portion of the waveform that fits in our display width
  // The waveform is scaled to fit the full duration
  const sampleWidth = fullWidth / samples.length;
  const startSampleIndex = Math.floor((trimStartPx / fullWidth) * samples.length);
  
  const centerY = height / 2;
  
  // Colors
  const activeColor = isMuted ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.85)';
  const trimColor = 'rgba(255, 255, 255, 0.2)'; // Dimmed for trimmed areas
  
  // Draw only the visible portion of the waveform
  // Start from where trimStart cuts off
  let currentX = 0;
  
  for (let i = startSampleIndex; i < samples.length && currentX < displayWidth; i++) {
    const sample = samples[i];
    const amplitude = Math.abs(sample - 128) / 128; // 0 to 1
    const barHeight = Math.max(2, amplitude * height * 0.85);
    
    const barWidth = Math.max(1, sampleWidth - 0.5);
    
    // Check if this sample is in the trimmed region (at the end)
    const sampleTimeFromStart = (i / samples.length) * fullDuration;
    const isInTrimmedEnd = sampleTimeFromStart > (fullDuration - track.trimEnd);
    
    ctx.fillStyle = isInTrimmedEnd ? trimColor : activeColor;
    
    // Draw the bar centered vertically
    ctx.fillRect(
      currentX,
      centerY - barHeight / 2,
      Math.min(barWidth, displayWidth - currentX),
      barHeight
    );
    
    currentX += sampleWidth;
  }
}

/**
 * Waveform Canvas Component
 */
function WaveformCanvas({ 
  track, 
  width, 
  height, 
  isMuted,
  pixelsPerSecond
}: { 
  track: AudioTrack; 
  width: number; 
  height: number;
  isMuted: boolean;
  pixelsPerSecond: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !track.waveform) return;
    
    // Set actual canvas size
    canvas.width = Math.max(1, Math.floor(width));
    canvas.height = height;
    
    drawWaveformOnCanvas(canvas, track, isMuted, width, pixelsPerSecond);
  }, [track, track.trimStart, track.trimEnd, width, height, isMuted, pixelsPerSecond]);
  
  if (!track.waveform) {
    return (
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.3,
        }}
      >
        <span style={{ fontSize: '10px', color: '#fff' }}>Loading...</span>
      </div>
    );
  }
  
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: width,
        height: height,
        pointerEvents: 'none',
      }}
    />
  );
}

/**
 * AudioTrack Component
 */
function AudioTrackItem({ track, pixelsPerSecond, isSelected, onSelect }: AudioTrackProps) {
  const timelineRef = useRef<HTMLDivElement>(null);
  
  // Track drag state for position
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartTime, setDragStartTime] = useState(0);
  
  // Track resize state
  const [isResizing, setIsResizing] = useState<'left' | 'right' | null>(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartTrim, setResizeStartTrim] = useState(0);
  
  // Store actions
  const updateAudioTrackPosition = useProjectStore((state) => state.updateAudioTrackPosition);
  const updateAudioTrack = useProjectStore((state) => state.updateAudioTrack);
  
  // Calculate effective values accounting for trim
  const effectiveStartTime = track.startTime;
  const effectiveDuration = track.duration - track.trimStart - track.trimEnd;
  const startX = effectiveStartTime * pixelsPerSecond;
  const width = Math.max(effectiveDuration * pixelsPerSecond, 60); // Min width for controls
  
  // Handle mouse down for dragging
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    onSelect();
    
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragStartTime(track.startTime);
  }, [onSelect, track.startTime]);
  
  // Handle resize handle mouse down
  const handleResizeStart = useCallback((e: React.MouseEvent, side: 'left' | 'right') => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    onSelect();
    
    setIsResizing(side);
    setResizeStartX(e.clientX);
    setResizeStartTrim(side === 'left' ? track.trimStart : track.trimEnd);
  }, [onSelect, track.trimStart, track.trimEnd]);
  
  // Handle mouse move for dragging and resizing
  useEffect(() => {
    if (!isDragging && !isResizing) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const deltaX = e.clientX - dragStartX;
        const deltaTime = deltaX / pixelsPerSecond;
        const newTime = Math.max(0, dragStartTime + deltaTime);
        updateAudioTrackPosition(track.id, newTime);
      } else if (isResizing) {
        const deltaX = e.clientX - resizeStartX;
        const deltaTime = deltaX / pixelsPerSecond;
        
        if (isResizing === 'left') {
          // Left handle: adjusts trimStart AND moves clip position
          // As we trim from start, the clip moves right to stay anchored to the content
          const newTrimStart = Math.max(0, Math.min(
            resizeStartTrim + deltaTime,
            track.duration - track.trimEnd - 0.5
          ));
          const trimDelta = newTrimStart - track.trimStart;
          updateAudioTrack(track.id, { 
            trimStart: newTrimStart,
            startTime: track.startTime + trimDelta
          });
        } else {
          // Right handle: adjusts trimEnd
          const newTrimEnd = Math.max(0, Math.min(
            resizeStartTrim - deltaTime,
            track.duration - track.trimStart - 0.5
          ));
          updateAudioTrack(track.id, { trimEnd: newTrimEnd });
        }
      }
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(null);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    isDragging, isResizing, dragStartX, dragStartTime, resizeStartX, resizeStartTrim,
    pixelsPerSecond, track.id, track.trimStart, track.trimEnd, track.duration, track.startTime,
    updateAudioTrackPosition, updateAudioTrack
  ]);
  
  // Handle volume change
  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const volume = parseFloat(e.target.value);
    updateAudioTrack(track.id, { volume });
  }, [track.id, updateAudioTrack]);
  
  // Handle mute toggle
  const handleMuteToggle = useCallback(() => {
    updateAudioTrack(track.id, { muted: !track.muted });
  }, [track.id, track.muted, updateAudioTrack]);

  return (
    <div
      ref={timelineRef}
      style={{
        position: 'absolute',
        left: startX,
        top: 7,
        width: width,
        height: 36,
        backgroundColor: isSelected
          ? 'var(--nb-accent-primary)'
          : track.muted ? '#444' : '#555',
        borderRadius: 'var(--nb-radius-sm)',
        cursor: isDragging ? 'grabbing' : isResizing ? (isResizing === 'left' ? 'w-resize' : 'e-resize') : 'grab',
        opacity: isDragging || isResizing ? 0.8 : 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        display: 'flex',
        alignItems: 'center',
        zIndex: isDragging || isResizing ? 100 : 10,
        minWidth: 60,
        transition: 'background-color var(--nb-transition-fast)',
        overflow: 'hidden',
      }}
      onMouseDown={handleMouseDown}
      title={`${track.name} (${effectiveDuration.toFixed(1)}s / ${track.duration.toFixed(1)}s)`}
    >
      {/* Waveform visualization - shows the trimmed portion of the anchored waveform */}
      <div
        style={{
          position: 'absolute',
          left: 4,
          right: 4,
          top: 2,
          bottom: 14, // Leave room for bottom controls
          pointerEvents: 'none',
        }}
      >
        <WaveformCanvas
          track={track}
          width={width - 8}
          height={20}
          isMuted={track.muted}
          pixelsPerSecond={pixelsPerSecond}
        />
      </div>
      
      {/* Left resize handle */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '8px',
          cursor: 'w-resize',
          backgroundColor: isResizing === 'left' ? 'var(--nb-accent-primary)' : 'transparent',
          opacity: isSelected ? 1 : 0,
          transition: 'opacity var(--nb-transition-fast), background-color var(--nb-transition-fast)',
          zIndex: 20,
        }}
        onMouseDown={(e) => handleResizeStart(e, 'left')}
        title="Drag to trim start"
      >
        <div
          style={{
            position: 'absolute',
            left: '2px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '2px',
            height: '16px',
            backgroundColor: '#fff',
            borderRadius: '1px',
            opacity: isSelected ? 0.8 : 0,
          }}
        />
      </div>
      
      {/* Right resize handle */}
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '8px',
          cursor: 'e-resize',
          backgroundColor: isResizing === 'right' ? 'var(--nb-accent-primary)' : 'transparent',
          opacity: isSelected ? 1 : 0,
          transition: 'opacity var(--nb-transition-fast), background-color var(--nb-transition-fast)',
          zIndex: 20,
        }}
        onMouseDown={(e) => handleResizeStart(e, 'right')}
        title="Drag to trim end"
      >
        <div
          style={{
            position: 'absolute',
            right: '2px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '2px',
            height: '16px',
            backgroundColor: '#fff',
            borderRadius: '1px',
            opacity: isSelected ? 0.8 : 0,
          }}
        />
      </div>
      
      {/* Track name overlay (at bottom) */}
      <span 
        style={{ 
          position: 'absolute',
          left: 8,
          bottom: 2,
          fontSize: '9px', 
          color: '#fff',
          fontWeight: 500,
          textShadow: '0 1px 2px rgba(0,0,0,0.9)',
          opacity: width > 150 ? 1 : 0,
          transition: 'opacity var(--nb-transition-fast)',
          pointerEvents: 'none',
          zIndex: 5,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: width - 100,
        }}
        title={track.name}
      >
        {track.name}
      </span>
      
      {/* Volume control (at bottom right) */}
      <div 
        style={{ 
          position: 'absolute',
          right: 28,
          bottom: 2,
          display: 'flex', 
          alignItems: 'center', 
          gap: '4px', 
          zIndex: 5,
          opacity: width > 120 ? 1 : 0,
          transition: 'opacity var(--nb-transition-fast)',
          pointerEvents: width > 120 ? 'auto' : 'none',
        }}
      >
        <style>{`
          .audio-volume-slider {
            -webkit-appearance: none;
            appearance: none;
            background: transparent;
            cursor: pointer;
          }
          .audio-volume-slider::-webkit-slider-runnable-track {
            background: #444;
            height: 4px;
            border-radius: 2px;
          }
          .audio-volume-slider::-moz-range-track {
            background: #444;
            height: 4px;
            border-radius: 2px;
          }
          .audio-volume-slider::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            background: #888;
            height: 10px;
            width: 10px;
            border-radius: 50%;
            margin-top: -3px;
            border: none;
          }
          .audio-volume-slider::-moz-range-thumb {
            background: #888;
            height: 10px;
            width: 10px;
            border-radius: 50%;
            border: none;
          }
          .audio-volume-slider:hover::-webkit-slider-thumb {
            background: #aaa;
          }
          .audio-volume-slider:hover::-moz-range-thumb {
            background: #aaa;
          }
        `}</style>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={track.volume}
          onChange={handleVolumeChange}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => (e.target as HTMLInputElement).blur()}
          tabIndex={-1}
          className="audio-volume-slider"
          style={{ width: '40px' }}
          title={`Volume: ${Math.round(track.volume * 100)}%`}
        />
        <span style={{ fontSize: '9px', color: '#fff', minWidth: '20px', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
          {Math.round(track.volume * 100)}%
        </span>
      </div>
      
      {/* Mute button */}
      <button
        onClick={handleMuteToggle}
        onMouseDown={(e) => e.stopPropagation()}
        tabIndex={-1}
        style={{
          position: 'absolute',
          right: 4,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '12px',
          padding: '2px',
          opacity: track.muted ? 0.5 : 1,
          zIndex: 5,
          textShadow: '0 1px 2px rgba(0,0,0,0.8)',
        }}
        title={track.muted ? 'Unmute' : 'Mute'}
      >
        {track.muted ? '🔇' : '🔊'}
      </button>
      
    </div>
  );
}

export default memo(AudioTrackItem);
